// Compose → preview → send, for one site invitation.
//
// The body leaves here with {{password}} and {{publicUrl}} still unresolved: the access
// password is minted server-side inside the send transaction, so the browser never sees
// it and the preview masks it. The API substitutes both just before the mail goes out.
//
// The PI is the recipient; the CRC is only ever a Cc, and that is a per-send choice
// defaulting to included. The API mails the CRC a copy with the password masked, so only
// the PI can actually open the questionnaire.
//
// A resend opens on the message this site was actually sent last time, not on the study
// template: the wording was tailored for this site once, and retyping it is how a resend
// ends up saying something different from the mail it replaces.
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { sfqApi } from "../../../api/feasibility";
import EmailComposer from "./EmailComposer";
import {
  DEFAULT_TEMPLATES, buildPreviewVars, hasRequiredVars,
  effectiveExpiry, expiryWillRenew,
} from "./emailTemplates";
import {
  Backdrop, Modal, Header, Title, CloseBtn, Body, Footer, Btn,
  LangTabs, LangTab, Loading, Warning,
} from "./emailModalUI";

const To = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 6px;
  b { color: #212529; font-weight: 600; }
`;

const CrcToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 12px;
  color: ${(p) => (p.$disabled ? "#adb5bd" : "#495057")};
  cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
  input { cursor: inherit; margin: 0; }
`;

const Hint = styled.div`
  margin-bottom: 10px;
  padding: 8px 10px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #f8f9fa;
  font-size: 11px;
  line-height: 1.6;
  color: #6c757d;
  b { color: #495057; }
`;

const Reopened = styled.div`
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  background: #e7f1ff;
  border-left: 3px solid #0d6efd;
  font-size: 12px;
  color: #084298;
  line-height: 1.5;
  b { font-weight: 600; }
`;

const LANGS = [
  { key: "ko", label: "한국어" },
  { key: "en", label: "English" },
];

const fmtSentAt = (v) =>
  v ? new Date(v).toLocaleString("ko-KR", {
        year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
      })
    : "";

const fmtExpiry = (v) =>
  new Date(v).toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" });

export default function SendInvitationModal({
  sfqId, inv, publicUrl, fromName, isResend, onSend, onClose,
}) {
  const [lang, setLang]         = useState("ko");
  const [templates, setTemplates] = useState(null); // per-lang starting point
  const [drafts, setDrafts]     = useState(null);   // per-lang, as edited here
  const [sending, setSending]   = useState(false);
  // { lang, sentAt } when the drafts were seeded from a previous send rather than the
  // template — the operator needs to know which of the two they are looking at.
  const [reopened, setReopened] = useState(null);
  // The PI receives the invitation; the CRC is opt-out, defaulting to copied so an
  // unchanged workflow still keeps the CRC in the loop — as a Cc, now.
  const [includeCrc, setIncludeCrc] = useState(true);

  // Two starting points, loaded together: the study's saved templates (falling back to the
  // shipped defaults per language), and — on a resend — the message this site was last
  // sent, which wins for its own language. The previous message is asked for only on a
  // resend; on a first send there is nothing to reopen, and the template is the point.
  //
  // Neither call is allowed to leave the dialog empty: a study that never customized its
  // template and a site whose earlier sends predate message capture both come back blank,
  // and the shipped default is still a sendable invitation.
  useEffect(() => {
    let alive = true;
    const invId = inv.id;

    Promise.all([
      sfqApi.getEmailTemplate(sfqId).then((res) => res.data.data || {}).catch(() => ({})),
      isResend
        ? sfqApi.getLastSentMail(sfqId, invId).then((res) => res.data.data || null).catch(() => null)
        : Promise.resolve(null),
    ]).then(([saved, last]) => {
      if (!alive) return;

      const seed = {
        ko: saved.ko ? { subject: saved.ko.subject, bodyHtml: saved.ko.bodyHtml } : { ...DEFAULT_TEMPLATES.ko },
        en: saved.en ? { subject: saved.en.subject, bodyHtml: saved.en.bodyHtml } : { ...DEFAULT_TEMPLATES.en },
      };
      setTemplates(seed);

      const next = { ko: { ...seed.ko }, en: { ...seed.en } };
      if (last?.bodyHtml) {
        // Sends made before the language was recorded land on the Korean tab — the
        // default the dialog opens on anyway.
        const prevLang = last.lang === "en" ? "en" : "ko";
        next[prevLang] = {
          subject:  last.subject || seed[prevLang].subject,
          bodyHtml: last.bodyHtml,
        };
        setLang(prevLang);
        setReopened({ lang: prevLang, sentAt: last.sentAt });
      }
      setDrafts(next);
    });

    return () => { alive = false; };
  }, [sfqId, inv.id, isResend]);

  // Edits are kept per language, so switching tabs to compare wording never discards work.
  function patch(next) {
    setDrafts((d) => ({ ...d, [lang]: { ...d[lang], ...next } }));
  }

  function resetToTemplate() {
    if (!window.confirm("편집한 내용을 버리고 템플릿 원본으로 되돌립니다. 계속할까요?")) return;
    setDrafts((d) => ({ ...d, [lang]: { ...templates[lang] } }));
    // This language no longer shows the previous message, so the notice must go with it.
    setReopened((r) => (r && r.lang === lang ? null : r));
  }

  const vars = useMemo(
    () => buildPreviewVars({ inv, publicUrl, fromName, lang }),
    [inv, publicUrl, fromName, lang]
  );

  // The link this mail carries is the one the site clicks: sending renews an expiry that
  // has already passed, so the mail never goes out pointing at a dead link. Said here
  // because the row still shows the old date until the send comes back.
  const renewsExpiry = expiryWillRenew(inv);
  const newExpiry    = fmtExpiry(effectiveExpiry(inv));

  // Same address in both fields is one person, already the To recipient — the API drops
  // the Cc in that case, so don't promise one here.
  const norm      = (v) => String(v || "").trim().toLowerCase();
  const crcIsPi   = !!inv.crcEmail && norm(inv.crcEmail) === norm(inv.piEmail);
  const sendToCrc = !!inv.crcEmail && !crcIsPi && includeCrc;

  const draft   = drafts?.[lang];
  // No PI address, no send: the access password is only ever mailed to the PI, so a
  // CRC-only message would carry a link nobody may open. The API refuses it too.
  const canSend = !!draft?.subject?.trim() && hasRequiredVars(draft?.bodyHtml)
                  && !!inv.piEmail && !sending;

  async function handleSend() {
    if (!canSend) return;
    setSending(true);
    try {
      await onSend({ subject: draft.subject, bodyHtml: draft.bodyHtml, lang, includeCrc });
    } finally {
      setSending(false);
    }
  }

  return (
    <Backdrop>
      <Modal $wide>
        <Header>
          <Title>
            {isResend ? "초대 메일 재발송" : "초대 메일 발송"} — {inv.siteName || inv.siteCode || `#${inv.id}`}
          </Title>
          <CloseBtn onClick={onClose} disabled={sending}>×</CloseBtn>
        </Header>

        <Body>
          <To>받는 사람: <b>{inv.piEmail || "(PI 이메일 없음)"}</b></To>
          <To>참조: <b>{sendToCrc ? inv.crcEmail : "(없음)"}</b></To>

          <CrcToggle $disabled={!inv.crcEmail || crcIsPi || sending}>
            <input
              type="checkbox"
              checked={sendToCrc}
              disabled={!inv.crcEmail || crcIsPi || sending}
              onChange={(e) => setIncludeCrc(e.target.checked)}
            />
            CRC를 참조(Cc)로 추가
            {!inv.crcEmail
              ? " (등록된 CRC 이메일 없음)"
              : crcIsPi
                ? ` (${inv.crcEmail} — PI와 같은 주소라 참조하지 않습니다)`
                : ` (${inv.crcEmail})`}
          </CrcToggle>

          <Hint>
            설문 접속 <b>비밀번호는 PI에게만</b> 발송됩니다. 참조로 받는 CRC의 메일에는
            비밀번호가 가려져 있어, 설문 작성과 제출은 <b>PI 본인만</b> 할 수 있습니다.
          </Hint>

          {!inv.piEmail && (
            <Warning>
              이 사이트에는 <b>PI 이메일</b>이 등록되어 있지 않아 발송할 수 없습니다.
              Edit에서 PI 이메일을 입력한 뒤 다시 시도해 주세요.
            </Warning>
          )}

          {isResend && (
            <Warning>
              재발송하면 <b>새 비밀번호가 발급</b>되어 이 사이트에 이전에 보낸 비밀번호는 더 이상 사용할 수 없습니다.
              설문 버전은 처음 발송한 버전이 그대로 유지됩니다.
            </Warning>
          )}

          {renewsExpiry && (
            <Reopened>
              이 사이트의 링크 만료일이 이미 지났습니다. 발송하면 만료일이{" "}
              <b>{newExpiry}</b>로 갱신되어, 이 메일의 링크는 그때까지 열립니다.
              다른 날짜로 하려면 발송을 닫고 Edit에서 만료일을 지정하세요.
            </Reopened>
          )}

          {reopened && reopened.lang === lang && (
            <Reopened>
              <b>{fmtSentAt(reopened.sentAt)}</b>에 이 사이트로 보낸 메일 내용을 불러왔습니다.
              그대로 재발송하거나 수정한 뒤 보내실 수 있으며, 스터디 템플릿으로 되돌리려면
              아래 <b>템플릿 원본으로</b>를 누르세요.
            </Reopened>
          )}

          <LangTabs>
            {LANGS.map((l) => (
              <LangTab key={l.key} type="button" $active={lang === l.key} onClick={() => setLang(l.key)}>
                {l.label}
              </LangTab>
            ))}
          </LangTabs>

          {!drafts ? (
            <Loading>템플릿을 불러오는 중...</Loading>
          ) : (
            <EmailComposer
              sfqId={sfqId}
              subject={draft.subject}
              bodyHtml={draft.bodyHtml}
              onChange={patch}
              vars={vars}
            />
          )}
        </Body>

        <Footer>
          <Btn type="button" onClick={resetToTemplate} disabled={!drafts || sending}>템플릿 원본으로</Btn>
          <Btn type="button" onClick={onClose} disabled={sending}>취소</Btn>
          <Btn type="button" $primary onClick={handleSend} disabled={!canSend}>
            {sending ? "발송 중..." : isResend ? "재발송" : "발송"}
          </Btn>
        </Footer>
      </Modal>
    </Backdrop>
  );
}
